'use client'

import useResizeObserver from '@/hooks/useResizeObserver'
import { Box } from '@/styled-system/jsx'
import { type HTMLStyledProps } from '@/styled-system/types'
import { memo, useRef } from 'react'
import KenoTile from './tile'

export type KenoTilesProps = HTMLStyledProps<'div'> & {
  selectedNumbers: Set<number>
  drawnNumbers: Set<number>
  isDisabled?: boolean
  onClickTile?: (number: number) => void
}

const KenoTiles = memo(function KenoTiles({
  selectedNumbers,
  drawnNumbers,
  isDisabled,
  onClickTile,
  ...props
}: KenoTilesProps) {
  const ref = useRef<HTMLDivElement>(null)

  useResizeObserver(ref, (entries) => {
    const { width } = entries[0].contentRect
    const target = entries[0].target as HTMLElement
    target.style.fontSize = `${width / 780}em`
  })

  return (
    <Box
      ref={ref}
      position='relative'
      display='grid'
      gridTemplateColumns='repeat(8, auto)'
      gap='0.75em'
      w='full'
      maxW='780px'
      {...props}
    >
      {Array.from(Array(40)).map((_, index) => {
        const number = index + 1
        const isSelected = selectedNumbers.has(number)
        const isRevealed = drawnNumbers.has(number)

        let kind: 'hidden' | 'selected' | 'revealed' | 'match'

        if (isSelected && isRevealed) {
          kind = 'match'
        } else if (isSelected) {
          kind = 'selected'
        } else if (isRevealed) {
          kind = 'revealed'
        } else {
          kind = 'hidden'
        }

        return (
          <KenoTile
            key={number}
            number={number}
            kind={kind}
            disabled={
              isDisabled || (!isSelected && selectedNumbers.size === 10)
            }
            onClick={() => onClickTile?.(number)}
          />
        )
      })}
    </Box>
  )
})

export default KenoTiles
